import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { LatLngExpression, Icon } from 'leaflet'
import { Company } from '@/services/api'
import { Link } from 'react-router-dom'
import 'leaflet/dist/leaflet.css'
import { MapPinIcon } from '@heroicons/react/24/outline'

// Fix Leaflet default icon issue with Vite
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png'

const DefaultIcon = new Icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
})

// Component to recenter map when user location changes
function RecenterMap({ center }: { center: LatLngExpression }) {
  const map = useMap()

  useEffect(() => {
    map.setView(center, map.getZoom())
  }, [center, map])

  return null
}

interface MapViewProps {
  companies: Company[]
  userLocation: { lat: number; lng: number }
  showContactInfo?: boolean
  buildDetailUrl?: (slug: string) => string // Function to build company detail URL with context
  onMarkerClick?: (company: Company) => void
  zoom?: number
  height?: string
}

const MapView: React.FC<MapViewProps> = ({
  companies,
  userLocation,
  showContactInfo = false,
  buildDetailUrl,
  onMarkerClick,
  zoom = 12,
  height = '600px'
}) => {
  const center: LatLngExpression = [userLocation.lat, userLocation.lng]

  // Filter companies with valid coordinates
  const validCompanies = companies.filter(
    c => c.latitude && c.longitude &&
    c.latitude >= 5 && c.latitude <= 21 &&
    c.longitude >= 97 && c.longitude <= 106
  )

  return (
    <div className="relative">
      {/* Warning if some companies don't have coordinates */}
      {companies.length > validCompanies.length && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <p className="flex items-center gap-2">
            <MapPinIcon className="w-4 h-4" />
            <span>
              บริษัท {companies.length - validCompanies.length} แห่ง ไม่มีพิกัดบนแผนที่
              (แสดงใน <strong>โหมดรายการ</strong> เท่านั้น)
            </span>
          </p>
        </div>
      )}

      {/* Map Container */}
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{ height, width: '100%', borderRadius: '12px' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <RecenterMap center={center} />

        {/* User Location Marker */}
        <Marker
          position={center}
          icon={new Icon({
            iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjM0I4MkY2Ij48Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSI4IiBmaWxsPSIjM0I4MkY2IiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMiIvPjwvc3ZnPg==',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -16]
          })}
        >
          <Popup>
            <div className="text-center">
              <p className="font-semibold text-primary-600">ตำแหน่งของคุณ</p>
            </div>
          </Popup>
        </Marker>

        {/* Company Markers */}
        {validCompanies.map((company) => {
          if (!company.latitude || !company.longitude) return null

          return (
            <Marker
              key={company.id}
              position={[company.latitude, company.longitude]}
              icon={DefaultIcon}
              eventHandlers={{
                click: () => {
                  onMarkerClick?.(company)
                }
              }}
            >
              <Popup>
                <div className="min-w-[250px]">
                  {/* Company Name */}
                  <Link
                    to={buildDetailUrl ? buildDetailUrl(company.slug) : `/companies/${company.slug}`}
                    className="font-semibold text-base text-primary-600 hover:text-primary-700 mb-2 block"
                  >
                    {company.nameTh}
                  </Link>

                  {/* Distance Badge */}
                  {company.distanceKm !== undefined && (
                    <div className="mb-2">
                      <span className="inline-block px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                        ห่างจากคุณ {company.distanceKm.toFixed(1)} กม.
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  <p className="text-sm text-neutral-600 mb-2">
                    📍 {company.district}, {company.province}
                  </p>

                  {/* Service Types */}
                  {company.serviceTypes && company.serviceTypes.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs text-neutral-500 mb-1">ประเภทบริการ:</p>
                      <div className="flex flex-wrap gap-1">
                        {company.serviceTypes.slice(0, 2).map((type, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 bg-neutral-100 text-neutral-700 text-xs rounded"
                          >
                            {type}
                          </span>
                        ))}
                        {company.serviceTypes.length > 2 && (
                          <span className="text-xs text-neutral-500">
                            +{company.serviceTypes.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contact Info (MEMBER only) */}
                  {showContactInfo && company.phone1 && (
                    <div className="mt-2 pt-2 border-t border-neutral-200">
                      <p className="text-sm">
                        📞 <a href={`tel:${company.phone1}`} className="text-primary-600 hover:underline">
                          {company.phone1}
                        </a>
                      </p>
                    </div>
                  )}

                  {/* View Details Button */}
                  <div className="mt-3">
                    <Link
                      to={buildDetailUrl ? buildDetailUrl(company.slug) : `/companies/${company.slug}`}
                      className="inline-block w-full text-center px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 transition-colors"
                    >
                      ดูรายละเอียด
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          )
        })}
      </MapContainer>

      {/* Map Stats */}
      <div className="mt-3 text-sm text-neutral-600 text-center">
        แสดง {validCompanies.length} บริษัท บนแผนที่
      </div>
    </div>
  )
}

export default MapView
